import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { Observable, Subject, takeUntil, tap} from 'rxjs';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import Icons from 'src/app/core/enums/icons.enum';
import { PortalApp } from 'src/app/core/shared/portal-app';
import { AppGroup, Application } from 'src/portaljs/constants/application';
import { AuthService } from 'src/app/core/services/auth.service';
import * as _ from 'lodash-es';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import PopupState from '../../core/enums/popup.state.enum';
import { ClientLoginService } from '../../core/services/client-login.service';
import { ToastMessageService } from 'src/app/core/services/toast-message.service';
import {MLMarketingURLUtils} from '../../core/shared/masterlibrary_marketing_urls';
import { Portal, TemplatePortal } from '@angular/cdk/portal';
import { ScreenStateService } from 'src/app/core/services/screen-state.service';
import { ScreenState } from 'src/app/core/enums/sceen-state.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthenticationEvent } from 'src/app/core/models/authentication-event.model';
import LoginPrompt from 'src/app/core/enums/login-prompt.enum';
import AuthType from 'src/app/core/enums/auth.type.enum';
import { ConsoleLogger } from 'src/app/core/shared/console-logger';

@Component({
  selector: 'fss-portal-app-switcher',
  templateUrl: './app-switcher.component.html',
  styleUrls: ['./app-switcher.component.scss'],
  standalone: false
})
export class AppSwitcherComponent implements OnInit, OnDestroy {
  private ngUnsubscribe = new Subject();
  // private _viewContainerRef = inject(ViewContainerRef);
  public portal: Portal<any>;
  public appsByGroup: any = {};
  public isLoginPopupDisplayed = false;
  public icons = Icons;
  public mlAppId: string = undefined;
  public readonly MOBILE_HEADER_TEXT = 'Switch Apps';
  public isDestinyAdmin = false;

  @ViewChild('appSwitcherTemplate') public appSwitcherTemplate: TemplateRef<any>;
  @ViewChild('appMenu') appMenu: MatMenu;
  @ViewChild('appSwitchTrigger') public appSwitchTrigger: MatMenuTrigger;
  @ViewChildren(MatMenuItem) public appButtons: MatMenuItem[];

  public selectableApps$: Observable<PortalApp[]> = this.portalStateService.getSelectableApps$()
    .pipe(
      tap(rez => {
        const visibleApps = rez.filter((app: PortalApp) => !app.internal && app.app !== Application.DESTINY_AI );
        this.appsByGroup = _.groupBy(visibleApps, (data: PortalApp) => {
          if([Application.FOLLETT_COMMUNITY].indexOf(data.app) >= 0 )
            return AppGroup.SUPPORT;
          else if([Application.DESTINY_EXPRESS, Application.TITLEWAVE].indexOf(data.app) >= 0 )
            return AppGroup.SHOP;
          else if([Application.MASTERLIBRARY_HELPDESK, Application.MASTERLIBRARY_ITAM].indexOf(data.app) >= 0 )
            return AppGroup.TECHNOLOGY;
          else if([Application.MASTERLIBRARY_SCHEDULES, Application.MASTERLIBRARY_DRAWINGS, Application.MASTERLIBRARY_WORKORDERS].indexOf(data.app) >= 0 )
            return AppGroup.FACILITIES;
          else
            return AppGroup.DESTINY;
        });
      })
  );

  public get isMobile(): boolean {
    return this.toolbarService.isMobile();
  }

  constructor(
    private screenStateService: ScreenStateService,
    private portalStateService: PortalStateService,
    private authService: AuthService,
    private readonly toolbarService: ToolbarService,
    private clientLoginService: ClientLoginService,
    private toastService: ToastMessageService,
    private _viewContainerRef: ViewContainerRef,
    private router: Router,
    private route: ActivatedRoute) {
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public ngOnInit(): void {

    this.authService.getUserLogin$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe( userLogin => {
        if (userLogin && userLogin.isDestinyAdmin()) {
          this.isDestinyAdmin = true;
        } else {
          this.isDestinyAdmin = false;
        }
      });


    this.screenStateService.screenState$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((state) => {
        if (this.isUnEnabledForCurrentApp()) {
          if (this.appSwitchTrigger && this.appSwitchTrigger.menuOpen && state === ScreenState.Mobile) {
            this.appSwitchTrigger.closeMenu();
            this.showInSidenav();
          }
          if (this.portal && state !== ScreenState.Mobile && this.toolbarService?.portal?.title === this.MOBILE_HEADER_TEXT) {
            this.portal = undefined;
            this.toolbarService.portal = undefined;
            setTimeout(() => this.appSwitchTrigger.openMenu());
          }
        }
      });

    this.selectableApps$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe();

    this.clientLoginService.getPopupState$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe( state => {
        this.isLoginPopupDisplayed = ( state === PopupState.LOGIN );
      });

    this.authService.getMLAppId$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe( mlAppId => {
        this.mlAppId = mlAppId;
    });
  }

  public isUnEnabledForCurrentApp() {
    return this.portalStateService.isUnEnabledForCurrentApp();
  }

  public isDDorCollections() {
    return this.portalStateService.getActiveApp().app === Application.DD || this.portalStateService.getActiveApp().app === Application.COLLECTIONS;
  }

  public get AppGroup() {
    return [AppGroup.DESTINY, AppGroup.SHOP, AppGroup.TECHNOLOGY, AppGroup.FACILITIES, AppGroup.SUPPORT];
  }

  public showInSidenav() {
    this.portal = new TemplatePortal(this.appSwitcherTemplate, this._viewContainerRef);
    this.toolbarService.portal = {templatePortal: this.portal, title: this.MOBILE_HEADER_TEXT};
  }

  public focusActiveApp() {
    this.appButtons.forEach((button: MatMenuItem) => {
      if (button['_elementRef'].nativeElement.innerText === this.getCurrentAppName()) {
        button.focus();
      }
    });
  }

  public checkActiveApp(app: any) {
    const activeApp = this.portalStateService.getActiveApp()?.app;
    if (this.isDestinyAdmin && !this.authService.getSiteGuid()) {
      if (activeApp === Application.PORTAL && app === 'District Home') {
        return activeApp === Application.PORTAL;
      } else if (activeApp === Application.DESTINY && app === 'District Setup') {
        return activeApp === Application.DESTINY;
      } else if (activeApp === Application.DESTINY_AI && app === 'Destiny AI') {
        return activeApp === Application.DESTINY_AI;
      }
    }

    return activeApp === app;
  }

  public appChange(app: PortalApp) {
    this.toolbarService.isMobileMenuOpen = false;
    this.toolbarService.portal = undefined;
    if (app.isExternal) {
      if (app.app === Application.DISCOVER_MANAGER) {
        const url = this.authService.getDiscoverAdminLaunchUrl();
        return window.open(url, '_blank');
      } else if ([Application.TITLEWAVE, Application.FM].indexOf(app.app) >= 0) {
        this.authService.launchToVendor(app.app as (Application.TITLEWAVE | Application.FM));
      } else if ([Application.MASTERLIBRARY_DRAWINGS, Application.MASTERLIBRARY_SCHEDULES, Application.MASTERLIBRARY_WORKORDERS, Application.MASTERLIBRARY_HELPDESK, Application.MASTERLIBRARY_ITAM].indexOf(app.app) >= 0) {
        if(this.mlAppId){
          this.authService.launchToMLVendor(app.app as (Application.MASTERLIBRARY_DRAWINGS | Application.MASTERLIBRARY_SCHEDULES | Application.MASTERLIBRARY_WORKORDERS | Application.MASTERLIBRARY_HELPDESK | Application.MASTERLIBRARY_ITAM));
        } else {
          const marketingUrl = MLMarketingURLUtils.getMarketingURL(app.app);
          return window.open(marketingUrl, '_blank');
        }
      } else {
        return window.open(app.url, '_blank');
      }
    } else {
      if (this.isUnEnabledForCurrentApp()) {
        this.portalStateService.setShowLoadingSpinner(true);
      }
      this.toastService.dismiss();
      if (Application.COLLECTIONS === this.portalStateService.getActiveApp()?.app) {
        this.portalStateService.sendPassivate(app);
        this.portalStateService.sendActivate(app);
      }
      this.portalStateService.clickFromAppMenu = true; 
      this.portalStateService.setActiveApp(app);
    }
    return undefined;
  }

  public closeSidenav() {
    this.toolbarService.closeSidenavOnMenuOpen();
  }

  public getAppName(app: Application) {
    return this.portalStateService.getAppName(app);
  }
  public getCurrentAppName() {
    const activeApp = this.portalStateService.getActiveApp()?.app;
    if (activeApp === Application.PORTAL && !this.authService.getSiteGuid()) {
      return 'District Home';
    }

    if (!activeApp || activeApp === Application.UNIFIED_SETTINGS || activeApp === Application.PORTAL) {
      return '';
    }
    return this.portalStateService.getAppName(activeApp);
  }


  public async gotoDestinySetup() {
    ConsoleLogger.debug('gotoDestinySetup');
    if (this.authService.getSiteGuid()) {
      const queryParams = {
        app: Application.DESTINY,
        appId: this.authService.getUserLogin()?.getAppId()
      };
      await this.router.navigate([], {relativeTo: this.route, queryParams}); // clean up query params

      this.authService.setSiteGuid(undefined);
      await this.portalStateService.setActiveAppFromApplication(Application.DESTINY);
      this.authService.initiateAuthentication(new AuthenticationEvent(LoginPrompt.NONE, AuthType.SITE_SWITCH, undefined, 'destinysetupnositeguid'));
    } else {
      const pa: PortalApp = this.portalStateService.getSelectableApps().find(s => s.app === Application.DESTINY);
      await this.portalStateService.setActiveApp(pa, {destination: pa.app, source:Application.PORTAL, route: 'DestinyAdminSetup'});
    }
  }


  public shouldShowDestinyAi(): boolean {
    return this.portalStateService.shouldShowDestinyAiInAppPicker();
  }
  public goToDestinyAi() {
    this.portalStateService.goToDestinyAi();
  }

  public goToDistrictHomePage() {
    this.portalStateService.goToDistrictHomePage();
  }
}
